import { useAuth0 } from "@auth0/auth0-react";
import { ArrowPathIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { isConnectedOrConnecting, isOrgEhrUsable } from "@utils/ehrActions";
import { formatDate } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiOnboardingFormStatusStatusChoices,
  EhrConnectionStatus,
  LearnersQuery,
  useLearnersQuery,
} from "@api/graphql/types-and-hooks";
import { ClientTable, FronteraClient } from "@components/ClientTable/ClientTable";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import {
  dismissImportingToast,
  notifyImportSuccess,
  notifyImporting,
  notifyRefreshSuccess,
  notifyRefreshing,
} from "@components/notifications/notifications";
import { useClientListImport } from "@hooks/useClientListImport";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { isStatusMaskedOrgType } from "@pages/Onboarding/ehrConnectionState";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";
import { CreateClientDialog } from "@routes/ClientDashboard/CreateClientDialog";
import { numberOfOnboardingSectionsCompleted } from "@routes/ClientDashboard/helpers";

const learnersToFronteraClient = (learners: LearnersQuery["learner"]): (FronteraClient | null)[] => {
  return (
    learners?.map((learner) => {
      const intakePages = learner.formStatusForLearner.edges.map((edge) => edge?.node);
      const intakeFinished = intakePages.find(
        (node) => node?.status === ApiOnboardingFormStatusStatusChoices.Completed && node.formName === "submit",
      );

      const intakeInProgress =
        intakePages.filter((page) => page?.status === ApiOnboardingFormStatusStatusChoices.Completed).length > 0;

      const intakeStarted = !!learner?.relationshipSet?.edges?.[0]?.node?.caregiver.auth0User?.auth0id;

      const intakePagesCompleted = numberOfOnboardingSectionsCompleted(intakePages || []);

      if (!learner.learnermetadata) return null;
      const { createdDate, id, hasCaregiverConsent } = learner;
      const { firstName, lastName, diagnosisAppointmentDate } = learner.learnermetadata!;
      const nearestLocation =
        learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.nearestLocation || "";
      const base64DecodedLearnedId = atob(learner.id);
      const databaseId = base64DecodedLearnedId.split("LearnerType:")?.pop();

      const caregivers: { id: string; name: string; consentGiven: boolean }[] = [];
      learner.relationshipSet.edges.forEach((edge) => {
        const relationship = edge?.node;
        const caregiver = edge?.node?.caregiver;
        const meta = caregiver?.caregivermetadata;

        if (relationship && caregiver) {
          caregivers.push({
            id: caregiver.id,
            name: `${meta?.firstName || "N/A"} ${meta?.lastName || ""}`,
            consentGiven: relationship.hasCaregiverConsent,
          });
        }
      });

      const fronteraClient: FronteraClient = {
        id: id,
        clientId: databaseId || "<missing-database-id>",
        name: `${firstName} ${lastName}`,
        caregiver: caregivers?.[0]?.name || "N/A",
        created: new Date(createdDate),
        intake: intakeFinished ? "Complete" : intakeInProgress ? "In Progress" : intakeStarted ? "Started" : "Awaiting",
        intakePagesCompleted: intakePagesCompleted,
        nextAppointment: diagnosisAppointmentDate ? new Date(diagnosisAppointmentDate) : new Date(), // weather diagnoses or assessment else null
        stage: diagnosisAppointmentDate ? "Diagnosis Pending" : "Assessment Pending",
        nearestLocation,
        dob: formatDate(learner.learnermetadata.birthDate),
        hasCaregiverConsent,
        caregivers,
        archived: learner.archived ?? false,
      };
      return fronteraClient;
    }) || []
  );
};

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const ClientDashboard: React.FC = () => {
  const { pathname } = useLocation();
  const go = useNavigate();
  const flags = useFlags();
  const { isAuthenticated, user } = useAuth0();
  const queryClient = useQueryClient();
  const [isNewClientDialogOpen, setIsNewClientDialogOpen] = useState<boolean>(false);
  const {
    openEHRLogin,
    openEHRClientList,
    ehrConnectionStatus,
    isCRDataWarehouse,
    isOrgUsernamePassword,
    usesOrgSession,
    setEhrStatusOverride,
    isImportEHRClientList,
    hasCredentials,
    notifyLoginInitiated,
    retryConnection,
  } = useEHRConnection();
  const { captureEhrRefreshClicked } = useEhrAnalytics();
  const [isEHRLoginFromImport, setIsEHRLoginFromImport] = useState<boolean>(false);
  const retryActionAfterLoginRef = useRef<"import" | "refresh" | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [lastRefreshLabel, setLastRefreshLabel] = useState<string>("");
  const [isRefreshMenuOpen, setIsRefreshMenuOpen] = useState(false);
  const [isCRImportActive, setIsCRImportActive] = useState(false);
  const refreshMenuRef = useRef<HTMLDivElement>(null);

  const {
    isOnboardingEnabled,
    isFronteraHealthTypeOrg,
    isHippHealthUser,
    isMotivityUser,
    isPassageHealthUser,
    isFronteraEhrUser,
    organizationType,
    roles,
    isEhrSyncCleared,
  } = useAdminData();

  const isHippHealthSyncEnabled = isHippHealthUser && flags["hipphealth-sync"];

  const { isImporting, startImport } = useClientListImport({
    enabled: (isPassageHealthUser || isHippHealthSyncEnabled) && pathname === "/clients",
    // Hipp Health's backend status is still hardcoded CONNECTED, so it skips
    // polling; Passage Health now reports real status (PROA-2515).
    waitForStatusTransition: !isStatusMaskedOrgType(organizationType),
    isEhrSyncCleared,
    onSuccess: () => {
      refetch().catch((error) => {
        console.error("Error refetching learners after client import", error);
      });
    },
  });

  const learnersQuery = useLearnersQuery(
    {},
    {
      queryKey: ["learners", isAuthenticated, JSON.stringify(roles)],
      retry: false,
      enabled:
        isAuthenticated &&
        !!roles.length &&
        (roles.includes("admin") ||
          roles.includes("bcba") ||
          roles.includes("assessment-bcba") ||
          roles.includes("psychologist") ||
          roles.includes("evaluation-psychologist")),
      refetchOnWindowFocus: false,
    },
  );

  const { data, isFetching, refetch } = learnersQuery;

  // Detect in-progress import from backend status (survives page refresh)
  const isImportDetectedFromStatus = ehrConnectionStatus === EhrConnectionStatus.ImportingClientList;

  const lastActionTypeRef = useRef<"import" | "refresh">("import");

  const { startImport: startCRAction } = useClientListImport({
    enabled: isImportEHRClientList && pathname === "/clients",
    // INT-36: CentralReach orgs on shared credentials queue an IMPORT_CLIENTS
    // action on the org session instead of signalling a per-provider workflow.
    usesOrgSession,
    errorMessage:
      lastActionTypeRef.current === "refresh" ? "Error refreshing client list" : "Error importing client list",
    waitForStatusTransition: !isCRDataWarehouse,
    isEhrSyncCleared,
    onSuccess: () => {
      // Only show toast if action was started this session (not resumed after page refresh)
      if (isCRImportActive) {
        if (lastActionTypeRef.current === "import") {
          notifyImportSuccess();
        } else {
          notifyRefreshSuccess();
        }
      }
      setIsCRImportActive(false);
      setLastRefreshTime(new Date());
      queryClient.invalidateQueries({ queryKey: ["getNotifications"] });
      refetch().catch((error) => {
        console.error("Error refetching learners after CR action", error);
      });
    },
    onError: () => {
      if (isCRImportActive && lastActionTypeRef.current === "import") {
        dismissImportingToast();
      }
      setIsCRImportActive(false);
      setEhrStatusOverride(null);
      // Org-level admin account: refresh the connection so an invalid-credentials
      // failure surfaces on the top badge without a manual refresh.
      if (isOrgUsernamePassword) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      }
    },
    onLoginFailed: () => {
      // CredentialsFailed = actual credential failure (infra failures surface as
      // RECONNECTING and are retried automatically by the workflow).
      // Always prompt for new credentials, regardless of whether credentials
      // were previously stored.
      // Org-managed credential modes (data warehouse, org-level admin account)
      // have no per-provider login — never prompt for credentials.
      if (!isCRDataWarehouse && !isOrgUsernamePassword) {
        retryActionAfterLoginRef.current = lastActionTypeRef.current;
        setIsEHRLoginFromImport(true);
        // Reset so the success guard in onSuccess doesn't fire a toast for this
        // stale session. Non-Motivity paths call startCRImportAction in
        // onLoginStarted which sets this back to true for the new import.
        setIsCRImportActive(false);
      } else {
        setIsCRImportActive(false);
        // Org-level admin account: refresh the connection so the failed
        // credentials surface on the top badge without a manual refresh.
        if (isOrgUsernamePassword) {
          queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
        }
      }
    },
  });

  const startCRImportAction = useCallback(
    async (actionType: "import" | "refresh") => {
      // Managed-onboarding gate: check before any optimistic UI. Showing the
      // importing/refreshing toast and then letting startCRAction's own gate
      // reject synchronously leaves the toast stuck — isCRImportActive hasn't
      // re-rendered yet when the hook's onError closure reads it, so the
      // dismiss-on-error guard sees a stale `false` and never fires.
      if (!isEhrSyncCleared) return;
      lastActionTypeRef.current = actionType;
      setIsCRImportActive(true);
      if (actionType === "import") notifyImporting();
      else notifyRefreshing();
      await startCRAction();
    },
    [startCRAction, isEhrSyncCleared],
  );

  const handleImportClients = useCallback(() => {
    // Skip the login dialog when connected or actively connecting — hasCredentials
    // may lag behind ehrConnectionStatus (e.g. after connecting via the header badge,
    // the credentials query isn't invalidated until the next CONNECTED transition).
    if (
      !hasCredentials &&
      !isCRDataWarehouse &&
      !isOrgUsernamePassword &&
      !isConnectedOrConnecting(ehrConnectionStatus)
    ) {
      retryActionAfterLoginRef.current = "import";
      setIsEHRLoginFromImport(true);
      return;
    }
    // Show "Connecting to EHR" optimistically when triggering an import from a Disconnected
    // state with saved credentials — importClients will auto-reconnect via stored creds.
    if (ehrConnectionStatus === EhrConnectionStatus.Disconnected && hasCredentials) {
      setEhrStatusOverride(EhrConnectionStatus.LoggingIn);
    }
    startCRImportAction("import");
  }, [
    hasCredentials,
    isCRDataWarehouse,
    isOrgUsernamePassword,
    ehrConnectionStatus,
    setEhrStatusOverride,
    startCRImportAction,
  ]);

  // Hydrate last refresh time from localStorage once user identity is available
  useEffect(() => {
    if (!user?.sub) return;
    const stored = localStorage.getItem(`cr-last-refresh-time:${user.sub}`);
    if (stored) {
      const date = new Date(stored);
      setLastRefreshTime(date);
      setLastRefreshLabel(formatTimeAgo(date));
    }
  }, [user?.sub]);

  // Update "Last refresh X ago" label every minute and persist to localStorage
  useEffect(() => {
    if (!lastRefreshTime || !user?.sub) return;
    localStorage.setItem(`cr-last-refresh-time:${user.sub}`, lastRefreshTime.toISOString());
    setLastRefreshLabel(formatTimeAgo(lastRefreshTime));
    const interval = setInterval(() => {
      setLastRefreshLabel(formatTimeAgo(lastRefreshTime));
    }, 60000);
    return () => clearInterval(interval);
  }, [lastRefreshTime, user?.sub]);

  // Close refresh menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (refreshMenuRef.current && !refreshMenuRef.current.contains(event.target as Node)) {
        setIsRefreshMenuOpen(false);
      }
    };
    if (isRefreshMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isRefreshMenuOpen]);

  const handleCRRefresh = useCallback(async () => {
    captureEhrRefreshClicked("clients_page");
    setIsRefreshMenuOpen(false);
    await startCRImportAction("refresh");
  }, [startCRImportAction, captureEhrRefreshClicked]);

  const clients = learnersToFronteraClient(data?.learner).filter((client): client is FronteraClient => client != null);

  // Org-admin orgs surface the connected, active/in-progress, and CONNECTION_ERROR
  // states as "connected" on the EHR badge, and importing/refreshing restarts the
  // workflow (i.e. a retry). Let the user retry in any usable state, and only block
  // import when the org EHR is genuinely not set up.
  const isOrgEhrNotSetUp = isOrgUsernamePassword && !isOrgEhrUsable(ehrConnectionStatus);

  const renderCreateClientButton = () => {
    if (isFronteraEhrUser) {
      return (
        <div className="flex flex-col items-end gap-1">
          <Button
            text="Add client from Frontera EHR"
            buttonStyle="filled"
            variant="primary"
            size="md"
            disabled={!isEhrSyncCleared}
            onClick={() =>
              openEHRClientList((client) => {
                refetch().catch((error) => {
                  console.error("Error refetching learners after Frontera EHR import", error);
                });
                void go(`/clients/${client.id}`);
              })
            }
          />
          {!isEhrSyncCleared && <EhrSyncGatedNotice />}
        </div>
      );
    }
    if (
      isFronteraHealthTypeOrg || // false = has ehr
      (isHippHealthUser && !flags["hipphealth-sync"]) ||
      flags["allow-create-client-with-ehr"] // true allow client creation even with an ehr
    ) {
      return (
        <Button
          text="New Client"
          buttonStyle="filled"
          variant="primary"
          size="md"
          onClick={() => {
            if (isOnboardingEnabled) {
              setIsNewClientDialogOpen(true);
            } else {
              go("/clients/client-lite");
            }
          }}
        />
      );
    }
    return null;
  };

  // Refresh-menu item: which action to offer depends on the connection state.
  // Kept as if/return branches (rather than a nested ternary) so the intent of
  // each state is legible and SonarCloud's no-nested-ternary rule stays happy.
  const renderRefreshMenuContent = () => {
    // Managed-onboarding gate: while the org isn't cleared, every refresh-menu
    // action would touch the EHR. Show a single disabled "Refresh Data" with an
    // inline explanation (the dropdown has room), regardless of connection state.
    if (!isEhrSyncCleared) {
      return (
        <>
          <button
            type="button"
            disabled
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-primary rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh Data
          </button>
          <div className="px-4 pb-2">
            <EhrSyncGatedNotice />
          </div>
        </>
      );
    }

    const isProviderCredentialsFailed =
      ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed && !isCRDataWarehouse && !isOrgUsernamePassword;
    if (isProviderCredentialsFailed) {
      return (
        <button
          type="button"
          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-primary hover:bg-limestone-50 cursor-pointer rounded-lg"
          onClick={() => {
            setIsRefreshMenuOpen(false);
            retryActionAfterLoginRef.current = "refresh";
            setIsEHRLoginFromImport(true);
          }}
        >
          Update Credentials
        </button>
      );
    }

    const isNotSetUpWithoutCredentials =
      (ehrConnectionStatus === EhrConnectionStatus.Disconnected ||
        ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed) &&
      !hasCredentials;
    if (isNotSetUpWithoutCredentials) {
      // Org-managed credential modes have no per-provider connect flow — direct
      // the user to their administrator instead.
      if (isCRDataWarehouse || isOrgUsernamePassword) {
        return (
          <span className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-secondary rounded-lg">
            Contact your administrator
          </span>
        );
      }
      return (
        <button
          type="button"
          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-primary hover:bg-limestone-50 cursor-pointer rounded-lg disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!isEhrSyncCleared}
          onClick={() => {
            setIsRefreshMenuOpen(false);
            openEHRLogin();
          }}
        >
          Connect EHR
        </button>
      );
    }

    if (ehrConnectionStatus === EhrConnectionStatus.ConnectionError && hasCredentials) {
      return (
        <button
          type="button"
          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-primary hover:bg-limestone-50 cursor-pointer rounded-lg"
          onClick={() => {
            setIsRefreshMenuOpen(false);
            retryConnection();
          }}
        >
          <ArrowPathIcon className="h-4 w-4" />
          Try again
        </button>
      );
    }

    return (
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-text-primary hover:bg-limestone-50 cursor-pointer rounded-lg"
        onClick={handleCRRefresh}
      >
        <ArrowPathIcon className="h-4 w-4" />
        Refresh Data
      </button>
    );
  };

  return (
    <div className="flex w-full flex-col content-between items-center lg:flex-col lg:items-start">
      {pathname === "/clients" ? (
        <div className="mt-10 flex w-full items-center justify-between">
          <Headline size="medium">Clients</Headline>
          <div className="flex items-center gap-2">
            {renderCreateClientButton()}
            {isImportEHRClientList && (
              <>
                {isCRImportActive || isImportDetectedFromStatus ? (
                  <div className="flex items-center gap-1.5 text-sm text-text-secondary">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    <span>Refreshing</span>
                  </div>
                ) : lastRefreshTime && clients.length > 0 ? (
                  <div className="relative flex items-center gap-1.5" ref={refreshMenuRef}>
                    <span className="text-sm text-text-secondary">Last refreshed {lastRefreshLabel}</span>
                    <button
                      type="button"
                      className={`rounded-md p-1 text-text-secondary ${ehrConnectionStatus === EhrConnectionStatus.LoggingIn || ehrConnectionStatus === EhrConnectionStatus.Reconnecting ? "opacity-40 cursor-not-allowed" : "hover:bg-limestone-50 cursor-pointer"}`}
                      disabled={
                        ehrConnectionStatus === EhrConnectionStatus.LoggingIn ||
                        ehrConnectionStatus === EhrConnectionStatus.Reconnecting
                      }
                      onClick={() => setIsRefreshMenuOpen(!isRefreshMenuOpen)}
                    >
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </button>
                    {isRefreshMenuOpen && (
                      <div className="absolute right-0 top-full mt-1 w-44 rounded-lg border border-limestone-200 bg-white shadow-lg z-50">
                        {renderRefreshMenuContent()}
                      </div>
                    )}
                  </div>
                ) : null}
                {!isCRImportActive &&
                  !isImportDetectedFromStatus &&
                  !isFetching &&
                  (!lastRefreshTime || clients.length === 0) && (
                    <div className="flex flex-col items-end gap-1">
                      <Button
                        text="Import Clients"
                        appearance="primary"
                        disabled={
                          !isEhrSyncCleared ||
                          (isCRDataWarehouse && ehrConnectionStatus === EhrConnectionStatus.Disconnected) ||
                          isOrgEhrNotSetUp
                        }
                        onClick={handleImportClients}
                      />
                      {!isEhrSyncCleared && <EhrSyncGatedNotice />}
                    </div>
                  )}
              </>
            )}
            {(isPassageHealthUser || isHippHealthSyncEnabled) && (
              <>
                {isImporting ? (
                  <div className="flex flex-row gap-2 items-center px-4 py-2 bg-surface-secondary rounded-lg">
                    <Loader className="h-5 w-5 ml-0 mr-0" />
                    <Paragraph className="text-sm">Importing clients...</Paragraph>
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <Button
                      text="Import Clients"
                      appearance="primary"
                      disabled={!isEhrSyncCleared}
                      onClick={startImport}
                    />
                    {!isEhrSyncCleared && <EhrSyncGatedNotice />}
                  </div>
                )}
              </>
            )}
            {/*  will use later, hiding for now */}

            {/* <Button
              text={
                <div className="flex items-center">
                  <Small>Update</Small>
                  <ArrowPathIcon className={`ml-1 h-4 w-4 text-text-secondary ${isFetching ? "animate-spin" : ""}`} />
                </div>
              }
              appearance="link"
              onClick={() => {
                refetch();
              }}
            /> */}
          </div>
        </div>
      ) : null}
      {pathname === "/clients" ? (
        <ClientTable
          loading={isFetching || isCRImportActive || isImportDetectedFromStatus}
          clients={clients}
          onRowClick={(client) =>
            go(`/clients/${client.id}`, {
              state: {
                id: client.id,
                name: client.name,
                intake: client.intake,
                stage: client.stage,
              },
            })
          }
          onImport={
            isImportEHRClientList &&
            isEhrSyncCleared &&
            !(isCRDataWarehouse && ehrConnectionStatus === EhrConnectionStatus.Disconnected) &&
            !isOrgEhrNotSetUp
              ? handleImportClients
              : undefined
          }
          emptyMessage={
            isCRDataWarehouse && ehrConnectionStatus === EhrConnectionStatus.Disconnected ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Paragraph displayType="loud" colorType="primary" size="medium">
                  No clients yet
                </Paragraph>
                <Paragraph size="small" colorType="secondary" className="text-center w-[350px]">
                  Your organization&apos;s EHR connection needs to be configured. Please contact your administrator to
                  set up the data warehouse credentials.
                </Paragraph>
              </div>
            ) : isOrgEhrNotSetUp ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Paragraph displayType="loud" colorType="primary" size="medium">
                  No clients yet
                </Paragraph>
                <Paragraph size="small" colorType="secondary" className="text-center w-[350px]">
                  Your organization&apos;s EHR connection hasn&apos;t been set up yet. Please contact your organization
                  administrator to set it up.
                </Paragraph>
              </div>
            ) : undefined
          }
        />
      ) : (
        <Outlet
          context={{
            refetch,
          }}
        />
      )}
      {isNewClientDialogOpen ? (
        <CreateClientDialog isDialogOpen={isNewClientDialogOpen} setDialogOpen={setIsNewClientDialogOpen} />
      ) : null}
      {isEHRLoginFromImport && (
        <EHRLoginDialog
          open={isEHRLoginFromImport}
          setOpen={(open) => {
            setIsEHRLoginFromImport(open);
            if (!open) retryActionAfterLoginRef.current = null;
          }}
          setSuccessLogin={() => {}}
          onLoginStarted={() => {
            const retryAction = retryActionAfterLoginRef.current;
            retryActionAfterLoginRef.current = null;
            setIsEHRLoginFromImport(false);
            setEhrStatusOverride(EhrConnectionStatus.LoggingIn);
            // Mark login as initiated so the CONNECTED event in EHRConnectionProvider
            // re-validates getHasEhrCredentials once Temporal has saved the credentials.
            // Pass the action type so Motivity refresh retries use the refresh toast.
            notifyLoginInitiated(retryAction || "import");
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
              queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
            }, 1000);
            // Motivity auto-import is triggered by EHRConnectionProvider on Connected;
            // calling startCRImportAction here would queue a second import.
            if (!isMotivityUser) {
              startCRImportAction(retryAction || "import");
            }
          }}
        />
      )}
    </div>
  );
};
